import { useState, useMemo, useContext, useCallback, useRef, useEffect } from 'react';
import { Alert, Button, Icon, Loading, MultiSelect, Popover, PopoverContent, PopoverTrigger, Select, Spinner, TextArea } from '@aidkitorg/component-library';
import type { SelectOptionType } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../Localization';
import { AKMeta } from '@aidkitorg/types/lib/convoai/conversation';
import { ChatConversationContext } from '../Contexts/ChatContext';
import InterfaceContext from '../Context';
import { usePost } from '../API';
import { ChatSessionContext } from '../Contexts/ChatSessionContext';
import { useAsyncEffect } from '../Hooks/AsyncEffect';

export const FeedbackButtons = (props: AKMeta["feedback"] & {
  id: string,
  createdAt?: number,
  /**
   * Conversation the item belongs to. Defaults to `ChatSessionContext` (the AI
   * Coach chat). Pass explicitly when used outside a chat session, e.g. the
   * support draft reply box, where the conversation comes from the
   * `/support/generate_draft` response.
   */
  conversationId?: string,
  /**
   * Called after feedback is persisted. Use outside a `ChatConversationProvider`
   * to mirror the new feedback into local state so the controls re-render
   * (the `dispatch` below is a no-op without that provider).
   */
  onFeedbackChange?: (feedback: NonNullable<AKMeta['feedback']>) => void,
  beforeContent?: JSX.Element | null,
  afterContent?: JSX.Element | null,
}) => {
  const { dispatch } = useContext(ChatConversationContext);
  const updateFeedback = usePost('/coach/conversation/feedback');
  const { lang } = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const [additionalFeedbackInput, setAdditionalFeedbackInput] = useState<string>(props.additionalFeedback ?? "");
  const [showAdditional, setShowAdditional] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const { id, createdAt, conversationId: conversationIdProp, onFeedbackChange, beforeContent, afterContent, ...feedback } = props;
  const { conversationId: sessionConversationId } = useContext(ChatSessionContext);
  const conversationId = conversationIdProp ?? sessionConversationId;

  const FeedbackReasons = [
    { value: '', i18nKey: "feedback_select_reason" },
    { value: 'too_slow', i18nKey: "feedback_too_slow" },
    { value: 'not_accurate', i18nKey: "feedback_not_accurate" },
    { value: 'tone', i18nKey: "feedback_tone" },
    { value: 'other', i18nKey: "feedback_other" }
  ].map(
    option => ({
      value: option.value,
      label: L.ai_coach[option.i18nKey as keyof Omit<typeof L.ai_coach, 'phases' | 'dashboard' | 'document_signals' | 'summary_settings'>]
    })
  );

  const selectedReason = useMemo(() => {
    return FeedbackReasons.find(r => r.value === props.reason) ?? FeedbackReasons[0];
  }, [props.reason, lang]);

  const sendFeedback = useCallback(async (feedback: NonNullable<AKMeta['feedback']>) => {
    if (createdAt && conversationId) {
      await updateFeedback({
        conversationId,
        createdAt,
        feedback
      });
    }
    dispatch({
      type: "update_meta",
      id,
      meta: { feedback }
    });
    onFeedbackChange?.(feedback);
  }, [dispatch, updateFeedback, id, createdAt, conversationId, onFeedbackChange]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm">
        {beforeContent ?? null}
        <button
          className={`${feedback.rating === "up" ? "text-indigo-600 opacity-100" : "text-gray-600 opacity-75"} hover:opacity-100 transition ease-in-out duration-100`}
          onClick={async () => {
            await sendFeedback({
              ...feedback,
              reason: null,
              additionalFeedback: null,
              rating: props.rating === "up" ? undefined : "up"
            });
            setShowSuccess(true);
          }}>
          <Icon size="md" name="HandThumbUpIcon" type="solid" />
        </button>

        <button
          className={`${feedback.rating === "down" ? "text-indigo-600 opacity-100" : "text-gray-600 opacity-75"} hover:opacity-100 transition ease-in-out duration-100`}
          onClick={async () => {
            const newRating = props.rating === "down" ? undefined : "down";
            await sendFeedback({
              ...feedback,
              rating: newRating
            });
            setShowAdditional(newRating === "down");
            setShowSuccess(!!(feedback.reason && feedback.reason !== "other"));
          }}
        >
          <Icon size="md" name="HandThumbDownIcon" type="solid" />
        </button>
        {afterContent ?? null}
      </div>

      {(feedback.rating === "down" && showAdditional) && (
        <Alert variant="secondary" className="p-2">
          <Select
            
            label={L.ai_coach.feedback_tell_us_more}
            labelLocation="above"
            anchor={null} // <= this causes the dropdown options to be rendered NOT in a portal
            description={L.ai_coach.feedback_select_reason}
            options={selectedReason ? FeedbackReasons.slice(1) : FeedbackReasons}
            value={selectedReason}
            onChange={async (option) => {
              if (option.value) {
                await sendFeedback({
                  ...feedback,
                  reason: option.value
                });
                setShowSuccess(!!(option.value && option.value !== "other"));
              }
            }}
          />
          {props.reason === 'other' && <form
            className="flex flex-col gap-2"
            onSubmit={
              async e => {
                e.preventDefault();
                await sendFeedback({
                  ...feedback,
                  additionalFeedback: additionalFeedbackInput
                });
                setShowSuccess(true)
              }
            }>
            <TextArea
              rows={2}
              label={L.ai_coach.feedback_additional}
              value={additionalFeedbackInput}
              onChange={e => setAdditionalFeedbackInput(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                className="self-start"
                size="sm"
                disabled={!additionalFeedbackInput}
                type="submit"
              >
                {L.applicant.meeting.submit}
              </Button>
            </div>
          </form>}
        </Alert>
      )}
      {(showSuccess && feedback.rating) && <Alert variant="success" className="p-2">{L.ai_coach.feedback_thanks}</Alert>}
    </div>
  );
};

const CustomTagForm = ({ query, onCreate }: { query: string; onCreate: (option: SelectOptionType) => void; onCancel: () => void; }) => {
  const searchTags = usePost('/coach/conversation/tags/all');
  const L = useLocalizedStrings();

  const [existingFoundTags, setExistingFoundTags] = useState<SelectOptionType[]>([]);  
  const [loading, setLoading] = useState<boolean>(false);
  query = query.trim();  

  useEffect(() => { 
    const debouncedTimeout = setTimeout(() => { 
      if (!query || query.length < 3) return;
      (async () => {
        setLoading(true);
        const response = await searchTags({ query });        
        if (!('error' in response) && response.tags) {
          setExistingFoundTags(response.tags.map(tag => ({ label: tag, value: tag })));
        }
        setLoading(false);
      })();
    }, 300);
    if (!query) { 
      setExistingFoundTags([]);
    }
    return () => { 
      if (debouncedTimeout) clearTimeout(debouncedTimeout);      
    }
  }, [setExistingFoundTags, searchTags, query])
  
  const selectableTags = [
    ...(query ? [{ value: query, label: query }] : []),
    ...existingFoundTags.filter(tag => tag.value !== query)
  ];

  return (
    <div className="flex flex-col items-center -mx-4 -my-2">
      { 
        selectableTags.map(option => <div
          key={option.value}
          className="text-sm truncate w-full overflow-hidden p-2 hover:bg-[var(--ak-color-brand-blue-800)] hover:text-white cursor-pointer flex justify-between items-center gap-2"
          onClick={() => {
            option.label = option.label.toString().trim();
            option.value = option.value.trim();
            return onCreate(option);
          }}>
          <span className="truncate">{option.label}</span>
          <Button className="shrink-0" size="sm" variant="secondary">{L.support.tags.add_tag}</Button>
        </div>)
      }
      { loading && <Spinner size="xsmall" className="p-0" /> }
    </div>
  );
};  

const initialReviewTags = [
  { value: 'Missing from knowledge base', label: "Missing from knowledge base" },
  { value: 'Answer out of bounds', label: "Answer out of bounds" },
  { value: 'Incorrect', label: "Incorrect" }
];
export const ReviewerTags = ({ id, tags: initialTags = initialReviewTags, conversationId, createdAt }: { id: string, tags?: SelectOptionType[], conversationId?: string, createdAt?: number }) => {
  const { dispatch } = useContext(ChatConversationContext);
  const listTags = usePost('/coach/conversation/tags/list');
  const updateTags = usePost('/coach/conversation/tags/update');
  const L = useLocalizedStrings();
  
  const [tags, setTags] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);  
  const hasUserUpdate = useRef<boolean>(false);
  
  const handleAddCustomOption = (option: SelectOptionType) => {
    setTags(tags => {
      if (!tags.includes(option.value)) {
        return [...tags, option.value];
      }
      return tags;
    })
  };  
  
  // fetch initial tags
  useAsyncEffect(async () => {
    setFetching(true);
    const tagsResult = await listTags({ conversationItemIds: [id], conversationId, createdAt });
    if (!('error' in tagsResult) && tagsResult.tags) {
      setTags(tagsResult.tags[id]);
    }
    setFetching(false);
  }, [id]);
    
  useAsyncEffect(async (signal) => {
    if (!hasUserUpdate.current) return;
    hasUserUpdate.current = false;
    try {
      setSaving(true);
      await updateTags({ conversationItemId: id, tags, createdAt, conversationId }, signal);
      if (signal.aborted) return;
      dispatch({ type: 'update_meta', id, meta: { tags } });
    } finally {
      if (!signal.aborted) setSaving(false);
    }
  }, [tags]);
    
  const selectOptions = useMemo<SelectOptionType[]>(() => {
    return initialTags.concat(tags.filter(tag => !initialTags.find(option => option.value === tag)).map(tag => ({ label: tag, value: tag}))).map(opt => ({ label: opt.label, value: opt.value.trim() }));
  }, [tags, initialTags]);
  
  return <div className="flex items-start gap-2">
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`${tags.length > 0 ? 'text-red-600 opacity-100' : 'text-red-400 opacity-75'} hover:opacity-100 transition ease-in-out duration-100 mt-1 flex flex-row gap-1 items-center`}
          title={L.support.tags.flag_an_issue}
        >
          <Icon size="md" name="FlagIcon" type="solid" />
          <span className="text-xs truncate">{L.support.tags.flag_an_issue}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <div className="flex flex-col gap-2">
          <MultiSelect
            disabled={fetching}
            label={L.support.tags.add_tag}
            labelLocation="above"
            anchor={null}
            value={selectOptions.filter(option => tags.includes(option.value))}
            description={L.support.tags.select_or_type_tag}
            onChange={values => {
              hasUserUpdate.current = true;
              setTags(values.map(v => v.value))              
            }}
            options={selectOptions}
            onAddCustomOption={handleAddCustomOption}
            customAllowed={true}
            customOptionForm={CustomTagForm}
          />
        </div>
      </PopoverContent>
    </Popover>
    
    {tags.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {tags.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-200 text-red-800 rounded-full"
          >
            {tag}
            <button
              onClick={() => {
                hasUserUpdate.current = true;
                setTags(prev => prev.filter(t => t !== tag));                
              }}
              disabled={saving}
              className="rounded-full p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={L.support.tags.remove_tag}>
              <Icon size="sm" name="XMarkIcon" />
            </button>
          </span>
        ))}
      </div>
    )}
  </div>;
};
